<!--
  detailsModalHost: orchestrates which page component is shown inside detailsModal.
  Listens for openDetailsModal / openNestedDetailsModal events, manages the view stack
  (Back navigation), and mounts the matching child with modalMode.
-->
<template>
	<detailsModal
		v-model="showDetailsModal"
		:title="detailsHeader"
		:showBack="showDetailsBack"
		@close="closeDetailsModal"
		@back="backDetailsModal"
	>
	<viewAudit
		v-if="detailsView === 'viewAudit'"
		:key="'viewAudit-' + detailsParams.auditId"
		:auditId="detailsParams.auditId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<editAudit
		v-else-if="detailsView === 'createAudit'"
		:key="'createAudit'"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<editAudit
		v-else-if="detailsView === 'editAudit'"
		:key="'editAudit-' + detailsParams.auditId"
		:auditId="detailsParams.auditId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<startAudit
		v-else-if="detailsView === 'startAudit'"
		:key="'startAudit-' + detailsParams.auditId + '-' + (detailsParams.tasks || '')"
		:auditId="detailsParams.auditId"
		:tasks="detailsParams.tasks"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<auditControls
		v-else-if="detailsView === 'auditControls'"
		:key="'auditControls-' + detailsParams.auditId"
		:auditId="detailsParams.auditId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<auditFindings
		v-else-if="detailsView === 'auditFindings'"
		:key="'auditFindings-' + detailsParams.auditId"
		:auditId="detailsParams.auditId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<controlEdit
		v-else-if="detailsView === 'controlEdit'"
		:key="'controlEdit-' + detailsParams.taskId"
		:auditId="detailsParams.auditId"
		:taskId="detailsParams.taskId"
		:myTasks="detailsParams.myTasks"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<findingsInfo
		v-else-if="detailsView === 'findingsInfo'"
		:key="'findingsInfo-' + detailsParams.findingId"
		:findingId="detailsParams.findingId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<findingTaskEdit
		v-else-if="detailsView === 'findingTaskEdit'"
		:key="'findingTaskEdit-' + detailsParams.taskId"
		:findingId="detailsParams.findingId"
		:taskId="detailsParams.taskId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	</detailsModal>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { emitter } from '@/main';
import detailsModal from '@/components/detailsModal.vue';
import viewAudit from '@/pages/viewAudit.vue';
import editAudit from '@/pages/addNewAudit.vue';
import startAudit from '@/pages/startAudit.vue';
import auditControls from '@/pages/auditControls.vue';
import auditFindings from '@/pages/auditFindings.vue';
import controlEdit from '@/pages/controlEdit.vue';
import findingsInfo from '@/pages/findingsInfo.vue';
import findingTaskEdit from '@/pages/findingTaskEdit.vue';

export default defineComponent({
	name: 'detailsModalHost',
	components: {
		detailsModal,
		viewAudit,
		editAudit,
		startAudit,
		auditControls,
		auditFindings,
		controlEdit,
		findingsInfo,
		findingTaskEdit,
	},
	data(): any {
		return {
			showDetailsModal: false,
			detailsView: '',
			detailsParams: {},
			detailsHeader: '',
			detailsStack: [] as any[],
		};
	},
	computed: {
		showDetailsBack(): boolean {
			return this.detailsStack.length > 1;
		},
	},
	methods: {
		openDetailsModal(payload: any) {
			emitter.emit('deActiviteToolTip');
			const entry = {
				view: payload.view,
				params: payload.params || {},
				title: payload.title || '',
			};
			this.detailsStack = [entry];
			this.showDetailsModal = true;
			document.body.style.overflow = 'hidden';
			this.applyEntry(entry);
		},
		openNested(payload: any) {
			emitter.emit('deActiviteToolTip');
			const entry = {
				view: payload.view,
				params: payload.params || {},
				title: payload.title || this.detailsHeader,
			};
			this.detailsStack.push(entry);
			this.applyEntry(entry);
		},
		applyEntry(entry: any) {
			this.detailsView = entry.view;
			this.detailsParams = { ...entry.params };
			this.detailsHeader = entry.title || '';
		},
		setDetailsHeader(info: any) {
			if (typeof info === 'string') {
				this.detailsHeader = info;
				return;
			}
			this.detailsHeader = info?.title || info?.header || this.detailsHeader;
		},
		backDetailsModal() {
			if (this.detailsStack.length < 2) {
				this.closeDetailsModal();
				return;
			}
			this.detailsStack.pop();
			this.applyEntry(this.detailsStack[this.detailsStack.length - 1]);
		},
		closeDetailsModal() {
			const closedView = this.detailsView;
			const closedParams = { ...this.detailsParams };
			this.showDetailsModal = false;
			this.detailsView = '';
			this.detailsParams = {};
			this.detailsHeader = '';
			this.detailsStack = [];
			document.body.style.overflow = '';
			this.$nextTick(() => {
				emitter.emit('detailsModalClosed', { view: closedView, params: closedParams });
			});
		},
	},
	mounted() {
		emitter.on('openDetailsModal', this.openDetailsModal);
		emitter.on('openNestedDetailsModal', this.openNested);
		emitter.on('closeDetailsModal', this.closeDetailsModal);
	},
	beforeUnmount() {
		emitter.off('openDetailsModal', this.openDetailsModal);
		emitter.off('openNestedDetailsModal', this.openNested);
		emitter.off('closeDetailsModal', this.closeDetailsModal);
		this.closeDetailsModal();
	},
});
</script>
